import { createContext, useContext, useState, useMemo, type ReactNode } from "react";

interface DateRange {
  from: Date;
  to: Date;
}

interface DateRangeContextType {
  dateRange: DateRange;
  comparisonRange: DateRange;
  setDateRange: (range: DateRange) => void;
  presets: { label: string; getValue: () => DateRange }[];
  formatDate: (date: Date) => string;
}

function daysAgo(n: number): Date {
  const d = new Date();
  d.setDate(d.getDate() - n);
  d.setHours(0, 0, 0, 0);
  return d;
}

function today(): Date {
  const d = new Date();
  d.setHours(23, 59, 59, 999);
  return d;
}

function startOfDay(d: Date): Date {
  const r = new Date(d);
  r.setHours(0, 0, 0, 0);
  return r;
}

function formatDateStr(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

const DateRangeContext = createContext<DateRangeContextType>({
  dateRange: { from: daysAgo(7), to: today() },
  comparisonRange: { from: daysAgo(14), to: daysAgo(8) },
  setDateRange: () => {},
  presets: [],
  formatDate: formatDateStr,
});

export function DateRangeProvider({ children }: { children: ReactNode }) {
  const [dateRange, setDateRange] = useState<DateRange>({
    from: daysAgo(7),
    to: today(),
  });

  const comparisonRange = useMemo(() => {
    const days = Math.ceil((dateRange.to.getTime() - dateRange.from.getTime()) / (1000 * 60 * 60 * 24));
    const compFrom = new Date(dateRange.from);
    compFrom.setDate(compFrom.getDate() - days - 1);
    const compTo = new Date(dateRange.from);
    compTo.setDate(compTo.getDate() - 1);
    return { from: startOfDay(compFrom), to: compTo };
  }, [dateRange]);

  const presets = useMemo(() => [
    { label: "Today", getValue: () => ({ from: startOfDay(new Date()), to: today() }) },
    { label: "Yesterday", getValue: () => ({ from: daysAgo(1), to: daysAgo(1) }) },
    { label: "Last 7 Days", getValue: () => ({ from: daysAgo(7), to: today() }) },
    { label: "Last 14 Days", getValue: () => ({ from: daysAgo(14), to: today() }) },
    { label: "Last 30 Days", getValue: () => ({ from: daysAgo(30), to: today() }) },
    {
      label: "This Month", getValue: () => {
        const d = new Date();
        return { from: new Date(d.getFullYear(), d.getMonth(), 1), to: today() };
      }
    },
    {
      label: "Last Month", getValue: () => {
        const d = new Date();
        const first = new Date(d.getFullYear(), d.getMonth() - 1, 1);
        const last = new Date(d.getFullYear(), d.getMonth(), 0);
        return { from: first, to: last };
      }
    },
  ], []);

  return (
    <DateRangeContext.Provider value={{ dateRange, comparisonRange, setDateRange, presets, formatDate: formatDateStr }}>
      {children}
    </DateRangeContext.Provider>
  );
}

export function useDateRange() {
  return useContext(DateRangeContext);
}
